import { notification } from 'antd';
import { Fragment, PureComponent, ReactNode } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import getTrialOrderPrescription from '../../api/getTrialOrderPrescription';
import BookLabTests from '../../components/prescriptionPad/rightPanel/finishScreen/BookLabTests';
import { Prescription } from '../../store/types/prescription';
import { getAgeInYears } from '../../store/utils';
import { track } from '../../tracking';
import { captureError, captureErrorString } from '../captureError';
import { FetchingLoader } from '../heOnboarding/Utils';
import { labSteps as steps } from './Utils';
import { getMySingleDoctorId } from '../../store/idbHandler';

interface State {
    isFetchingPrescription?: boolean;
    prescription?: Prescription;
    activeStep: number;
    finishedSteps: Set<string>;
    isFetchError: boolean;
}

class PharmaStartPage extends PureComponent<RouteComponentProps, State> {
    initialState: State = {
        isFetchingPrescription: true,
        isFetchError: false,
        activeStep: 0,
        finishedSteps: new Set([]),
        prescription: undefined,
    };
    state: State = this.initialState;

    componentDidMount = async (): Promise<void> => {
        const { finishedSteps } = this.state;
        try {
            const docid = getMySingleDoctorId() || '';
            const { status, prescription } = await getTrialOrderPrescription(docid);
            if (status === 'success') {
                this.setState({
                    prescription,
                    activeStep: -1,
                    finishedSteps: finishedSteps.add('create-rx'),
                });
            } else {
                this.setState({
                    isFetchError: true,
                });
                captureErrorString('getTrialOrderPrescription failed');
                track('ERROR', {
                    on: 'getTrialOrderPrescription',
                    error: 'getTrialOrderPrescription failed',
                });
                notification.error({
                    message: 'Error while fetching cart details!',
                    duration: 5,
                });
            }
        } catch (error) {
            this.setState({
                isFetchError: true,
            });
            captureError(error as Error);
            track('ERROR', {
                on: 'getTrialOrderPrescription',
                error: (error as Error).message,
            });
            notification.error({
                message: 'Error while fetching cart details!',
                duration: 5,
            });
        } finally {
            this.setState({ isFetchingPrescription: false });
        }
    };

    closeMe = () => {
        console.log('closeMe');
    };

    changeSteps = (activeStep: number = -1) => {
        const { finishedSteps } = this.state;

        if (activeStep === -1) {
            steps.forEach((step) => finishedSteps.add(step.action));
            this.setState({ activeStep, finishedSteps });
            return;
        }

        if (activeStep === 0) {
            steps.forEach((step) => finishedSteps.delete(step.action));
            this.setState({ activeStep, finishedSteps });
            return;
        }

        const isStepAlreadyFinished = finishedSteps.has(steps[activeStep]?.action);
        if (isStepAlreadyFinished) {
            finishedSteps.delete(steps[activeStep].action);
        } else {
            finishedSteps.add(steps[activeStep - 1].action);
        }
        this.setState({
            activeStep,
            finishedSteps,
        });
    };

    onRefresh = () => {
        const { initialState, componentDidMount } = this;
        this.setState(initialState, componentDidMount);
    };

    render(): ReactNode {
        const {
            isFetchingPrescription,
            activeStep,
            finishedSteps,
            prescription,
            isFetchError,
        } = this.state;
        const { closeMe, changeSteps, onRefresh } = this;

        const ageObj = prescription?.patient.profile.personal.age || {};
        const age = getAgeInYears(ageObj) || 18;

        return (
            <Fragment>
                <Helmet>
                    <meta charSet="utf-8" />
                    <title>EKA - HE Trial Order</title>
                </Helmet>
                <FetchingLoader isFetchingProfile={isFetchingPrescription} />
                <div className="flex w-full h-full bg-white font-lato">
                    <div className="p-16 space-y-20" style={{ width: 320 }}>
                        <p className="font-700 text-18">Place trial order</p>

                        <div>
                            {steps.map((i, index) => (
                                <div
                                    className={`flex items-start space-x-8 p-8 pb-12 cursor-pointer ${
                                        i.action === steps[activeStep]?.action
                                            ? 'bg-darwin-accent-bright-blue-800 text-white font-700 rounded-4'
                                            : 'border-b border-darwin-neutral-200 text-darwin-neutral-800'
                                    }`}
                                    key={index}
                                >
                                    <div className="pt-3">
                                        {finishedSteps.has(i.action) ? (
                                            <svg
                                                className="w-16 h-16 fill-darwin-accent-bright-blue-800"
                                                viewBox="0 0 14 15"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path d="M7 14.75C3.11719 14.75 0 11.6328 0 7.75C0 3.89453 3.11719 0.75 7 0.75C10.8555 0.75 14 3.89453 14 7.75C14 11.6328 10.8555 14.75 7 14.75ZM10.0898 6.46484H10.0625C10.3359 6.21875 10.3359 5.80859 10.0625 5.53516C9.81641 5.28906 9.40625 5.28906 9.16016 5.53516L6.125 8.59766L4.83984 7.3125C4.56641 7.03906 4.15625 7.03906 3.91016 7.3125C3.63672 7.55859 3.63672 7.96875 3.91016 8.21484L5.66016 9.96484C5.90625 10.2383 6.31641 10.2383 6.58984 9.96484L10.0898 6.46484Z" />
                                            </svg>
                                        ) : (
                                            <svg
                                                className={`w-16 h-16 ${
                                                    i.action === steps[activeStep]?.action
                                                        ? 'fill-white'
                                                        : 'fill-darwin-neutral-800'
                                                }`}
                                                viewBox="0 0 14 15"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path d="M14 7.75C14 11.6328 10.8555 14.75 7 14.75C3.11719 14.75 0 11.6328 0 7.75C0 3.89453 3.11719 0.75 7 0.75C10.8555 0.75 14 3.89453 14 7.75ZM7 2.0625C3.85547 2.0625 1.3125 4.63281 1.3125 7.75C1.3125 10.8945 3.85547 13.4375 7 13.4375C10.1172 13.4375 12.6875 10.8945 12.6875 7.75C12.6875 4.63281 10.1172 2.0625 7 2.0625Z" />
                                            </svg>
                                        )}
                                    </div>
                                    <p>{i.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="border-l border-darwin-neutral-200 flex-1">
                        {isFetchError ? (
                            <div className="h-full w-full flex items-center justify-center">
                                <button
                                    className="flex items-center space-x-8 p-10 px-16 bg-darwin-primary-500 rounded-8 text-white font-700"
                                    onClick={onRefresh}
                                >
                                    <svg className="w-14 h-14 fill-white" viewBox="0 0 512 512">
                                        <path d="M370.72 133.28C339.458 104.008 298.888 87.962 255.848 88C178.39 88.068 111.52 141.178 93.057 214.85C91.713 220.213 86.935 224 81.406 224H24.103C16.605 224 10.909 217.193 12.296 209.824C33.933 94.924 134.813 8 256 8C322.448 8 382.791 34.136 427.315 76.685L463.03 40.97C478.149 25.851 504 36.559 504 57.941V192C504 205.255 493.255 216 480 216H345.941C324.559 216 313.851 190.149 328.97 175.029L370.72 133.28V133.28ZM32 296H166.059C187.441 296 198.149 321.851 183.03 336.971L141.28 378.721C172.542 407.994 213.115 424.04 256.156 424.001C333.574 423.931 400.471 370.857 418.943 297.152C420.287 291.789 425.065 288.002 430.594 288.002H487.898C495.396 288.002 501.092 294.809 499.705 302.178C478.067 417.076 377.187 504 256 504C189.552 504 129.209 477.864 84.685 435.315L48.97 471.03C33.851 486.149 8 475.441 8 454.059V320C8 306.745 18.745 296 32 296Z"></path>
                                    </svg>
                                    <span>Refresh</span>
                                </button>
                            </div>
                        ) : prescription ? (
                            <BookLabTests
                                closeMe={closeMe}
                                formData={[]}
                                pxid={prescription.id}
                                patientid={prescription.patient.id || ''}
                                name={prescription.patient.profile.personal.name}
                                phone={prescription.patient.profile.personal.phone?.n || ''}
                                gender={prescription.patient.profile.personal.gender}
                                age={age}
                                isForTrialOrder
                                changeSteps={changeSteps}
                            />
                        ) : null}
                    </div>
                </div>
            </Fragment>
        );
    }
}

export default connect()(withRouter(PharmaStartPage));
